import './Access.css';
import React, { useEffect, useState } from "react";
import ApiInterface from "@/commons/api/ApiInterface";
import Cookies from 'js-cookie';
import Header from "@/commons/pages/borders/header/Header";
import Footer from "@/commons/pages/borders/footer/Footer";
import { environment } from "@/commons/configs/Config";
import { sha256Encrypt } from "@/commons/crypt/Encryption";
import FloatingInput from "@/commons/components/fields/floatingInput/FloatingInput";
import CustomButton from "@/commons/components/buttons/customButton/CustomButton";
import {useUser} from "@/contexts/UserContext";
import {AccessTokenResponse} from "@/commons/api/objects/responses/auth/AccessTokenResponse";

const Access = () => {
    const [view, setView] = useState<'login' | 'register' | 'forgot'>('login');
    const {newRefreshToken} = useUser();


    function setErrorMessage(message: string) {
        const element = document.getElementById("accessMessage");
        if (!element) throw Error("Errore nel codice! Un elemento risulta Nullo!");
        element.innerHTML = "<span style='color:red'>" + message + "</span>";
    }

    useEffect(() => {
        const hash = window.location.hash;

        if (hash === "#login") setView('login');
        else if (hash === "#register") setView('register');
        else if (hash === "#forgot") setView('forgot');
        else setView('login');

        // Rimuove l'hash dalla URL (senza errori TS)
        window.history.replaceState(null, '', window.location.pathname);
    }, []);

    const handleKeyDown = (event: { key: string }) => {
        if (event.key === 'Enter') {
            if (view === 'register') registerSubmit();
            else if (view === 'login') loginSubmit();
            else if (view === 'forgot') forgotSubmit();
        }
    };

    function removeRead(event: React.FocusEvent<HTMLInputElement>) {
        event.target.removeAttribute("readonly");
    }

    async function updateLoginData() {
        const username = (document.getElementById("usernameLogin") as HTMLInputElement).value;
        const password = (document.getElementById("passwordLogin") as HTMLInputElement).value;
        try {
            const p = await sha256Encrypt(password);
            return { username, password: p };
        } catch (error) {
            console.error(error);
        }
    }

    async function loginSubmit() {
        const userLogin = document.getElementById("usernameLogin");
        const passwordLogin = document.getElementById("passwordLogin");
        const usernameAfter = document.getElementById("usernameLogin-After");
        const passwordAfter = document.getElementById("passwordLogin-After");

        fetchLogin().then(async r => {
            if (!userLogin || !passwordLogin || !usernameAfter || !passwordAfter) throw Error("Errore nel codice! Un elemento risulta Nullo!");
            if (r.status === 200) {
                let json = await r.json();
                let accessToken: AccessTokenResponse = new AccessTokenResponse(json);
                await newRefreshToken(accessToken);

                setTimeout(homeRedirect, 0);
            } else if (r.status === 404) {
                userLogin.classList.add("errorInput");
                usernameAfter.innerHTML = "<div class='exclamationMark'></div> Username non presente sul database";
            } else if (r.status === 401) {
                passwordLogin.classList.add("errorInput");
                passwordAfter.innerHTML = "<div class='exclamationMark'></div> Password non corrispondente a quella nel database";
            }
        }).catch((e) => {
            console.error(e)
            setErrorMessage("Si è verificato un errore interno!<br/>Se il problema persiste contatta gli Admin!");
        });
    }

    function homeRedirect() {
        window.location.href = "/";
    }

    async function fetchLogin() {
        const loginData = await updateLoginData();
        return await fetch(environment.apiUrlOne + '/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include', // 🔥 OBBLIGATORIO per ricevere i cookie
            body: JSON.stringify(loginData)
        });
    }

    async function updateRegisterData() {
        const username = (document.getElementById("usernameRegister") as HTMLInputElement).value;
        const password = (document.getElementById("passwordRegister") as HTMLInputElement).value;
        const email = (document.getElementById("email") as HTMLInputElement).value;
        try {
            const shaPassword = await sha256Encrypt(password);
            return { username, email, password: shaPassword };
        } catch (error) {
            console.error(error);
        }
    }

    async function registerSubmit() {
        const password = (document.getElementById("passwordRegister") as HTMLInputElement).value;
        const confirmPassword = (document.getElementById("confirmPassword") as HTMLInputElement).value;
        if (password !== confirmPassword) {
            setErrorMessage("Errore durante la registrazione!<br/>Le password non corrispondono!");
            return;
        }
        const registerData = await updateRegisterData();
        ApiInterface.fetchRegister(registerData).then(async r => {
            if (r.status === 200) {
                let json = await r.json();
                let accessToken: AccessTokenResponse = new AccessTokenResponse(json);
                await newRefreshToken(accessToken);

                setTimeout(homeRedirect, 0);
            } else if (r.status === 302) {
                const message = await r.text();
                const value = message.toLowerCase();
                await resetErrors();
                if (value === "username") {
                    const usernameElement = document.getElementById("usernameRegister");
                    if (!usernameElement) throw Error("Errore nel codice! Un elemento risulta Nullo!");
                    usernameElement.classList.add("inputError");
                }
                if (value === "email") {
                    const emailElement = document.getElementById("email");
                    if (!emailElement) throw Error("Errore nel codice! Un elemento risulta Nullo!");
                    emailElement.classList.add("inputError");
                }
                setErrorMessage("Email o Nome utente già registrati!");
            } else {
                setErrorMessage("Errore durante la registrazione!<br/>Potrebbe essere dovuto a un problema del server!");
            }
        }).catch(() => {
            setErrorMessage("Si è verificato un errore interno!<br/>Se il problema persiste contatta gli admin!");
        });
    }

    async function resetErrors() {
        const usernameElement = document.getElementById("usernameRegister");
        const emailElement = document.getElementById("email");
        if (!usernameElement || !emailElement) throw Error("Errore nel codice! Un elemento risulta Nullo!");
        usernameElement.classList.remove("inputError");
        emailElement.classList.remove("inputError");
    }

    async function forgotSubmit() {
        const emailInput = document.getElementById("emailForgot") as HTMLInputElement;
        const email = emailInput.value;
        const button = document.getElementById("sendButtonForgot") as HTMLButtonElement;

        button.disabled = true;
        button.classList.add("button--loading");

        if (!email) {
            setErrorMessage("Inserisci una email valida!");
            button.classList.remove("button--loading");
            button.disabled = false;
            return;
        }

        const forgotData = { userId: email, scope: "PASSWORD_RESET" };

        fetch(environment.apiUrlOne + '/token/create', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': ""
            },
            body: JSON.stringify(forgotData),
        }).then(async r => {
            if (r.status === 200) {
                const token = await r.text();
                Cookies.set('token', token, { expires: 30, path: '/', domain: 'eblcraft.it' });
                button.style.backgroundColor = '#307239';
                button.disabled = true;
                button.innerText = 'Inviato';
            } else if (r.status === 404) {
                setErrorMessage("L'Email non è registrata!");
                button.style.backgroundColor = '#7b3a3a';
                button.disabled = false;
            } else {
                setErrorMessage("Errore interno durante l'invio della mail!");
                button.style.backgroundColor = '#b34141';
                button.disabled = false;
            }
            button.classList.remove("button--loading");
        }).catch(() => {
            setErrorMessage("Si è verificato un errore interno!<br/>Se il problema persiste contatta gli admin!");
            button.style.backgroundColor = '#b34141';
            button.disabled = false;
            button.classList.remove("button--loading");
        });
    }

    return (
        <>
            <div className="page__body">
                <Header/>
                <div className="page__content">
                    <div className="mainBox">
                        <div className="innerBox" onKeyDown={handleKeyDown}>
                            <div id="accessMessage"></div>

                            {view === 'forgot' && (
                                <div className="formBox">
                                    <span className="typeAccess">RESET PASSWORD</span>
                                    <div className="formContainer">
                                        <FloatingInput type={"email"} label={"Email"} id={"emailForgot"}/>
                                    </div>
                                    <a className="tinyLine" onClick={() => setView('login')}>Vuoi effettuare il Login?</a><br />
                                    <CustomButton onClick={forgotSubmit} id={"sendButtonForgot"} children={"Invia"}/>
                                </div>
                            )}

                            {view === 'login' && (
                                <div className="formBox">
                                    <span className="typeAccess">ACCEDI</span>
                                    <div className="formContainer">
                                        <FloatingInput type={"text"} label={"Username"} id={"usernameLogin"}/>
                                        <span id="usernameLogin-After" className="inputAfterError"></span>
                                        <FloatingInput type={"password"} label={"Password"} id={"passwordLogin"}/>
                                        <span id="passwordLogin-After" className="inputAfterError"></span>
                                    </div>
                                    <CustomButton onClick={loginSubmit} children={"Accedi"}/><br />
                                    <a className="tinyLine" onClick={() => setView('forgot')}>Hai dimenticato la tua password?</a><br /><br />
                                    <hr />
                                    <span className="tinyLine">Non hai ancora un account? <a onClick={() => setView('register')}>Registrati qui</a></span><br /><br />
                                </div>
                            )}

                            {view === 'register' && (
                                <div className="formBox">
                                    <span className="typeAccess">REGISTRATI</span>
                                    <div className="formContainer">
                                        <FloatingInput type={"text"} label={"Username"} id={"usernameRegister"}/>
                                        <FloatingInput type={"email"} label={"Email"} id={"email"}/>
                                        <FloatingInput type={"password"} label={"Password"} id={"passwordRegister"}/>
                                        <FloatingInput type={"password"} label={"Conferma Password"} id={"confirmPassword"}/>
                                    </div><br />
                                    <div className="checkBoxContainer">
                                        <FloatingInput type={"checkbox"} label={<>
                                            Accetto i <a href="https://www.eblcraft.it/tos" target="_blank" rel="noreferrer">Termini e Condizioni d'uso</a> di questo sito
                                        </>} id={"termsAndCond"}/>
                                    </div>
                                    <CustomButton onClick={registerSubmit} children={"Registrati"}/><br /><br />
                                    <hr />
                                    <span className="tinyLine">Hai già un account? <a onClick={() => setView('login')}>Accedi qui</a></span><br /><br />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </>
    );
};

export default Access;
